import type {
  DiceFace,
  EquipmentColour,
  PowerKind,
} from "@cloudnest/celestia-engine";

/**
 * Celestia shared icon sprite — geometric hazard / power glyphs.
 *
 * Ported 1:1 from the canonical wireframe sprite
 * (docs/wireframes/celestia/02-components.html). Every game shape is encoded by
 * silhouette, not colour alone (WCAG 1.4.1). Mount ONCE per screen; dice, cards
 * and power chips reference symbols via `<use href="#ic-…">` (works across the
 * playground's SVG canvases and the HTML card faces alike).
 *
 * The only deviation from the wireframe source is `var(--panel)` → the equivalent
 * app token `var(--cn-celestia-bottom-sheet-bg)` (same #fbf6ea / #1d1b14); all
 * other fills are intrinsic glyph art (hardcoded) or `currentColor`.
 */

export type CelestiaIconId =
  | "ic-fog"
  | "ic-lightning"
  | "ic-birds"
  | "ic-pirate"
  | "ic-blank"
  | "ic-half"
  | "ic-turbo"
  | "ic-grapple"
  | "ic-jetpack"
  | "ic-altroute"
  | "ic-windgust"
  | "ic-spyglass";

/** Die face → hazard glyph. `fiftyFifty` reuses the half-disc mark. */
export const DICE_FACE_ICON: Readonly<Record<DiceFace, CelestiaIconId>> = {
  fog: "ic-fog",
  lightning: "ic-lightning",
  birds: "ic-birds",
  pirate: "ic-pirate",
  blank: "ic-blank",
  fiftyFifty: "ic-half",
};

/** Equipment colour → the hazard it cancels (spec §3.2). */
export const EQUIPMENT_ICON: Readonly<Record<EquipmentColour, CelestiaIconId>> =
  {
    blue: "ic-fog",
    yellow: "ic-lightning",
    red: "ic-birds",
    black: "ic-pirate",
  };

/** Power card → its glyph (spec §3.4). */
export const POWER_ICON: Readonly<Record<PowerKind, CelestiaIconId>> = {
  ejection: "ic-grapple",
  jetpack: "ic-jetpack",
  alternativeRoute: "ic-altroute",
  windGust: "ic-windgust",
};

export function CelestiaIconSprite() {
  return (
    <svg
      data-testid="celestia-icon-sprite"
      aria-hidden="true"
      focusable="false"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
    >
      <defs>
        <symbol id="ic-fog" viewBox="0 0 24 24">
          <g fill="currentColor">
            <circle cx="8.5" cy="13" r="4.2" />
            <circle cx="13" cy="10.4" r="5.4" />
            <circle cx="16.6" cy="13.4" r="3.6" />
            <rect x="5.4" y="14" width="13.4" height="4.6" rx="2.3" />
          </g>
        </symbol>
        <symbol id="ic-lightning" viewBox="0 0 24 24">
          <path
            d="M13.6 2 L5.4 13 H10.6 L8.4 22 L18.6 9.4 H12.9 Z"
            fill="#2a2208"
          />
        </symbol>
        <symbol id="ic-birds" viewBox="0 0 24 24">
          <path
            d="M3.4 12.6 Q5.4 5.4 13 6 Q19.1 6.4 20.5 11.3 Q21.1 14.7 16.8 15.5 Q14.6 19.6 9.3 18.5 Q4.9 17.4 3.4 12.6 Z"
            fill="#1b0c09"
          />
          <path d="M20.3 10.5 L24 12 L20 13.7 Z" fill="#1b0c09" />
          <circle cx="9" cy="11" r="1.5" fill="#ffffff" />
        </symbol>
        <symbol id="ic-pirate" viewBox="0 0 24 24">
          <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="5" y1="5" x2="19" y2="19" />
            <line x1="19" y1="5" x2="5" y2="19" />
          </g>
          <g fill="currentColor">
            <circle cx="5" cy="5" r="2.1" />
            <circle cx="19" cy="5" r="2.1" />
          </g>
        </symbol>
        <symbol id="ic-blank" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="8.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeDasharray="2 3"
            opacity="0.45"
          />
        </symbol>
        <symbol id="ic-half" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path d="M12 3 A9 9 0 0 1 12 21 Z" fill="currentColor" />
        </symbol>
        <symbol id="ic-turbo" viewBox="0 0 24 24">
          <path
            d="M12 12 L5 5 A10 10 0 0 1 19 5 Z"
            fill="var(--cn-celestia-event-lightning)"
          />
          <path
            d="M12 12 L19 5 A10 10 0 0 1 19 19 Z"
            fill="var(--cn-celestia-event-pirate)"
          />
          <path
            d="M12 12 L19 19 A10 10 0 0 1 5 19 Z"
            fill="var(--cn-celestia-event-fog)"
          />
          <path
            d="M12 12 L5 19 A10 10 0 0 1 5 5 Z"
            fill="var(--cn-celestia-event-birds)"
          />
          <circle
            cx="12"
            cy="12"
            r="10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <g stroke="currentColor" strokeWidth="1.1">
            <line x1="5" y1="5" x2="19" y2="19" />
            <line x1="19" y1="5" x2="5" y2="19" />
          </g>
        </symbol>
        <symbol id="ic-grapple" viewBox="0 0 24 24">
          <g
            fill="none"
            stroke="#8a5523"
            strokeWidth="2.4"
            strokeLinecap="round"
          >
            <line x1="12" y1="4" x2="12" y2="13" />
            <path d="M12 13 Q12 19 7 19 Q4.5 19 4.5 16.5" />
            <path d="M12 13 Q12 19 17 19 Q19.5 19 19.5 16.5" />
          </g>
          <circle
            cx="12"
            cy="4"
            r="2.2"
            fill="none"
            stroke="#8a5523"
            strokeWidth="2"
          />
        </symbol>
        <symbol id="ic-jetpack" viewBox="0 0 24 24">
          <rect x="7" y="3.5" width="10" height="13" rx="2.4" fill="#2f8a5e" />
          <rect
            x="9.4"
            y="5.6"
            width="5.2"
            height="8.6"
            rx="1"
            fill="#1f5e3f"
          />
          <path d="M8.6 16.5 L7 22 L10 17 Z" fill="#f5871f" />
          <path d="M15.4 16.5 L17 22 L14 17 Z" fill="#f5871f" />
        </symbol>
        <symbol id="ic-altroute" viewBox="0 0 24 24">
          <path
            d="M4.6 9 A9 9 0 1 1 4 14.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M4.6 9 L2.3 5.4 M4.6 9 L8.6 8.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect
            x="5.5"
            y="7"
            width="8"
            height="8"
            rx="1.8"
            fill="var(--cn-celestia-bottom-sheet-bg)"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <rect
            x="11"
            y="10.5"
            width="8"
            height="8"
            rx="1.8"
            fill="var(--cn-celestia-bottom-sheet-bg)"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <circle cx="13.3" cy="12.8" r="1" fill="currentColor" />
          <circle cx="16.7" cy="16.2" r="1" fill="currentColor" />
        </symbol>
        <symbol id="ic-windgust" viewBox="0 0 24 24">
          <path
            d="M4.6 9 A9 9 0 1 1 4 14.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M4.6 9 L2.3 5.4 M4.6 9 L8.6 8.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect
            x="5.5"
            y="7"
            width="8"
            height="8"
            rx="1.8"
            fill="var(--cn-celestia-bottom-sheet-bg)"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <rect
            x="11"
            y="10.5"
            width="8"
            height="8"
            rx="1.8"
            fill="var(--cn-celestia-bottom-sheet-bg)"
            stroke="currentColor"
            strokeWidth="1.6"
          />
        </symbol>
        <symbol id="ic-spyglass" viewBox="0 0 24 24">
          <g transform="rotate(-28 12 12)">
            <path d="M3 10 L9 9 L9 15 L3 14 Z" fill="#c98f26" />
            <path d="M9 8.4 L20 7 L20 17 L9 15.6 Z" fill="#e0b450" />
            <rect
              x="19.2"
              y="6.4"
              width="2.6"
              height="11.2"
              rx="1"
              fill="#b9822e"
            />
          </g>
        </symbol>
      </defs>
    </svg>
  );
}
